import { Spin } from "antd";
import i18n from "i18next";
import { type FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import { ActionBar } from "../../../../../components/ActionBar/ActionBar";
import { CSVImportButton } from "../../../../../components/FeatureButtons/CSVImportButton";
import { ExportButton } from "../../../../../components/FeatureButtons/ExportButton";
import { FormManager } from "../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { showSuccessNotification } from "../../../../../components/NotificationAlerts";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { useLoyaltyMessageLoadQuery, useLoyaltyMessageUpdateMutation } from "../../services/server/LoyaltyMessageApi";
import type { LoyaltyMessageEntity } from "../../types/LoyaltyMessageEntity";

export const LoyaltyMessageEditForm: FC<FormProps> = ({ runSubmit }) => {
    const navigate = useNavigate();

    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("loyalty-message");

    const [formData, setFormData] = useState<LoyaltyMessageEntity>({} as LoyaltyMessageEntity);
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useLoyaltyMessageLoadQuery({});
    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData, setFormData]);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useLoyaltyMessageUpdateMutation();
    const dispatch = useDispatch();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formUpdateSuccess", {
                            id: result.data.id,
                            resource: "LoyaltyMessage",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.LOYALTY_MESSAGES}`);
            }
        },
        [saveFormData, formData, navigate, dispatch],
    );

    const exportCallback = () => {
        const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(formData.loyaltyMessages ?? "")}`;
        const a = document.createElement("a");
        a.setAttribute("href", csvContent);
        a.download = `${ResourceName.LOYALTY_MESSAGES}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            <ActionBar className="left">
                <ExportButton callback={exportCallback} />
                <CSVImportButton isDisabled={false} resourceName={ResourceName.LOYALTY_MESSAGES} />
            </ActionBar>

            {formDefinition?.formConfiguration && (
                <FormManager<LoyaltyMessageEntity>
                    name="LoyaltyMessageEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.DEFAULT}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
